
import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { HeartPulse, BadgePercent, Shield, Gift } from 'lucide-react';

const BenefitsPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-green-50">
      <Helmet>
        <title>PEO Employee Benefits Solutions | The PEO App</title>
        <meta name="description" content="Provide Fortune 500-level benefits with PEO services. Learn how Professional Employer Organizations help small and medium-sized businesses offer competitive healthcare, retirement, and ancillary benefits." />
        <link rel="canonical" href="https://thepeoapp.com/services/benefits" />
        <meta name="keywords" content="PEO employee benefits, PEO health insurance, PEO retirement plans, small business benefits, PEO 401k plans, Professional Employer Organization benefits" />
      </Helmet>
      
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">PEO Employee Benefits Solutions</h1>
          
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed">
              One of the most compelling advantages of partnering with a Professional Employer Organization (PEO) is gaining access to Fortune 500-level employee benefits that would otherwise be unavailable or unaffordable for small and medium-sized businesses. Through their economies of scale, PEOs help level the playing field in the competition for talent.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="bg-white p-8 rounded-xl shadow-md">
              <HeartPulse className="h-12 w-12 text-[#00a86b] mb-6" />
              <h3 className="text-2xl font-semibold mb-4">Comprehensive Health Benefits</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Multiple health insurance plan options (PPO, HMO, HSA-compatible)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Dental and vision coverage</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Prescription drug plans</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Competitive premiums through large group buying power</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>ACA-compliant coverage options</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md">
              <BadgePercent className="h-12 w-12 text-[#00a86b] mb-6" />
              <h3 className="text-2xl font-semibold mb-4">Retirement & Financial Wellness</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>401(k) plans with reduced administrative costs</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Employer matching options</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Roth 401(k) options</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Financial wellness programs and education</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Student loan repayment assistance programs</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="bg-white p-8 rounded-xl shadow-md">
              <Shield className="h-12 w-12 text-[#00a86b] mb-6" />
              <h3 className="text-2xl font-semibold mb-4">Insurance & Protection</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Short and long-term disability insurance</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Life insurance plans</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Accidental death & dismemberment (AD&D) coverage</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Supplemental insurance options</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Critical illness coverage</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md">
              <Gift className="h-12 w-12 text-[#00a86b] mb-6" />
              <h3 className="text-2xl font-semibold mb-4">Additional Benefits & Perks</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Flexible spending accounts (FSAs) and health savings accounts (HSAs)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Employee assistance programs (EAP)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Wellness programs and incentives</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Commuter benefits</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00a86b] mt-2" aria-hidden="true"></span>
                  <span>Voluntary benefits and discount programs</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white p-8 rounded-xl shadow-lg mb-16">
            <h2 className="text-3xl font-bold mb-6">The PEO Benefits Advantage</h2>
            <div className="space-y-6">
              <p className="text-lg text-gray-700">
                Partnering with a PEO for employee benefits offers significant advantages for businesses:
              </p>
              
              <ul className="list-disc pl-6 space-y-3 text-gray-700">
                <li><strong>Cost Savings:</strong> Access lower rates typically only available to large corporations</li>
                <li><strong>Administrative Relief:</strong> Reduce the burden of benefits administration and compliance</li>
                <li><strong>Talent Attraction:</strong> Compete for top talent with Fortune 500-level benefits packages</li>
                <li><strong>Employee Retention:</strong> Increase satisfaction and loyalty with comprehensive benefits</li>
                <li><strong>Compliance Management:</strong> Stay compliant with ACA requirements and other benefits regulations</li>
                <li><strong>Expert Guidance:</strong> Receive support with benefits selection and enrollment from specialists</li>
              </ul>
            </div>
          </div>
          
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Ready to Offer Better Benefits?</h2>
            <Link to="/">
              <Button className="bg-[#00a86b] hover:bg-[#008f5a] text-white text-lg px-8 py-6 rounded-full">
                Get Free PEO Quotes
              </Button>
            </Link>
          </div>
          
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold mb-6">PEO Benefits FAQs</h2>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold mb-2">How can a small business afford quality benefits through a PEO?</h3>
                <p className="text-gray-700">
                  PEOs combine the employees of all their client companies into a large pool, creating economies of scale that result in access to better insurance rates and plan options. This collective buying power allows small businesses to offer benefits comparable to those of large corporations at more affordable rates.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2">Can my business customize the benefits package offered through a PEO?</h3>
                <p className="text-gray-700">
                  Yes, most PEOs offer flexible benefits options that allow you to select which benefits to offer and how much to contribute toward premiums. You can typically choose from multiple health plans, decide whether to offer dental and vision, and determine your level of contributions to retirement plans.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2">Will employees lose their current doctors when switching to PEO benefits?</h3>
                <p className="text-gray-700">
                  Not necessarily. PEOs typically offer multiple health insurance plans from major carriers with extensive provider networks. During the transition process, your PEO partner can help analyze provider networks to minimize disruption and help employees understand their options for continuing care with preferred providers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BenefitsPage;
